import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useAuth } from '../context/SimpleAuthContext';

/**
 * Material Login Page
 * 
 * Provides login functionality using the auth context
 * Redirects to dashboard if already authenticated
 */
export default function MaterialLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const { login, isAuthenticated, loading, error } = useAuth();
  const navigate = useNavigate();

  /**
   * Handle login form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validate form
    if (!email) {
      setFormError('Email is required');
      return;
    }
    if (!password) {
      setFormError('Password is required');
      return;
    }
    
    // Clear form error
    setFormError('');
    
    try {
      // Attempt login
      await login(email, password);
      // Explicitly navigate after successful login
      navigate('/', { replace: true });
    } catch (err) {
      // Error handled by auth context
    }
  };

  /**
   * Redirect to dashboard if already authenticated
   */
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Also keep the immediate redirect for already authenticated users
  if (isAuthenticated && !loading) {
    return <Navigate to="/" replace />;
  }

  /**
   * Render component
   */
  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography component="h1" variant="h5">
              ModularCompliance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compliance Management System
            </Typography>
          </Box>
          
          {/* Display errors */}
          {(error || formError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError || error}
            </Alert>
          )}
          
          {/* Login form */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email-input"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password-input"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              id="login-button"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
            
            <Divider sx={{ my: 2 }}>OR</Divider>
            
            <Button
              component={Link}
              to="/register"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
            >
              Create New Account
            </Button>
            
            {/* Development login help - only show in development */}
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                For development: admin@example.com / password
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
